import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { css } from "styled-components";
import { accentColorPrimary, accentColorDanger, accentColorConfirmation, accentColorWarning, backgroundColorLevelCurrent, black, grey15, textColorTertiary, borderColorPrimary, borderColorTertiary, spacing4, spacing8, spacing32, spacing64, fontSize16, textColorPrimary, lineHeight, sans, borderRadius, getAppearanceConstant, setAppearanceConstantLocal, whiteTransparency30, whiteTransparency70, grey35, colorScheme } from "@churchofjesuschrist/eden-style-constants";
import { AlertFilled, AlertWarningFilled, CircleSelectedFilled } from "@churchofjesuschrist/eden-icons";
import { dir } from "@churchofjesuschrist/eden-styled-helpers";
import { adjustHexAlpha } from "@churchofjesuschrist/eden-utilities";
import { jsx as _jsx } from "react/jsx-runtime";
export const [formElementDisabledBackgroundDef, formElementDisabledBackground] = setAppearanceConstantLocal("form-element-disabled-background", {
  light: adjustHexAlpha(black, 0.12),
  dark: whiteTransparency70,
  prefix: "eden"
});
export const [formElementDisabledBorderDef, formElementDisabledBorder] = setAppearanceConstantLocal("form-element-disabled-border", {
  light: grey15,
  dark: borderColorTertiary,
  prefix: "eden"
});
const [formElementDisabledSelectedBackgroundDef, formElementDisabledSelectedBackground] = setAppearanceConstantLocal("form-element-disabled-selected-background", {
  light: adjustHexAlpha(black, 0.38),
  dark: whiteTransparency30,
  prefix: "eden"
});
const formElementDisabledColor = textColorTertiary;

// When an SVG is used as a url() background-image, it acts as a separate document and does not have access to the parent document, almost like an iframe. This means that CSS variables declared on the parent are not available to it. This function takes an SVG and injects a style block into it that defines the needed CSS variable and related darkMode functionality based on the context that can be used in the SVG.
export const darkModeURLIcon = (Icon, {
  light,
  dark
}, curAppearanceContext,
// The validity icon needs to be colored (red/green), but a type indicator (date/time) should be grey. Allow setting if the fallback should be used for Safari.
useSafariFallback = false) => {
  // There is some issue with the parser that if we use a `var()` the closing paren causes a parsing error, so we have to use straight CSS without variables in the below theme overrides.
  // Default to light version
  let styleBlock = `path{ fill: ${curAppearanceContext?.mode === "dark" ? dark : light};}`;

  // If "os" we wrap it in a color-scheme check
  // @supports check is for safari only. They don't support prefers-color-scheme as part of a base64 encoded SVG (https://bugs.webkit.org/show_bug.cgi?id=199134) so we need to use a single color for both light and dark.
  styleBlock += curAppearanceContext?.mode === "os" ? `@media (prefers-color-scheme: dark) {
        path{fill: ${dark};}
}
${useSafariFallback ? `@supports (-webkit-hyphens:none) { path{fill: ${grey35};}` : ""}
` : ``;

  // Check if it was passed as an object or a string, if an object change it to a string
  const theIcon = typeof Icon === "object" ? renderToStaticMarkup(Icon) : Icon;
  return `url('data:image/svg+xml; utf8, ${encodeURIComponent(theIcon.replace(
  // find the close of the opening tag and the start of the next tag
  />\s*</,
  // inject the following style block just after the opening tag in the SVG
  `><style>${styleBlock.replace("  ", "")}</style><`))
  // parens don't get replaced with encodeURIComponent, but appear to throw off any styling after it for some reason, so let's force them to be encoded
  .replaceAll("(", "%28").replaceAll(")", "%29")}')`;
};
const errorBackground = /*#__PURE__*/_jsx(AlertFilled, {
  size: "1.5rem"
});
const warningBackground = /*#__PURE__*/_jsx(AlertWarningFilled, {
  size: "1.5rem"
});
const successBackground = /*#__PURE__*/_jsx(CircleSelectedFilled, {
  size: "1.5rem"
});
export const valuesByValidityState = {
  error: {
    borderColor: accentColorDanger,
    background: errorBackground,
    modeStyle: getAppearanceConstant(accentColorDanger)
  },
  warning: {
    borderColor: borderColorPrimary,
    background: warningBackground,
    modeStyle: getAppearanceConstant(accentColorWarning)
  },
  success: {
    borderColor: borderColorPrimary,
    background: successBackground,
    modeStyle: getAppearanceConstant(accentColorConfirmation)
  }
};
export const internalUseOnlyOpenDetailsStyles = ({
  $icon,
  $curAppearanceContext,
  $validityState
}) => {
  const {
    background,
    modeStyle
  } = valuesByValidityState[$validityState] || {};
  return css(["background-image:", " ", ";&:disabled{background-image:", " ", ";}padding-inline-end:", ";background-position:", " right ", " center;background-size:", " 1.5rem 1.5rem;", ""], background ? `${darkModeURLIcon(background, modeStyle, $curAppearanceContext)},` : "", darkModeURLIcon($icon, getAppearanceConstant(textColorPrimary), $curAppearanceContext, true), background ? `${darkModeURLIcon(background, modeStyle, $curAppearanceContext)},` : "", darkModeURLIcon($icon, getAppearanceConstant(formElementDisabledColor), $curAppearanceContext), background ? spacing64 : spacing32, background ? `right ${spacing32} center,` : "", spacing8, background ? "1.5rem 1.5rem," : "", dir("rtl")`
      background-position: ${background ? `left ${spacing32} center,` : ""} left ${spacing8} center;

      /* Switches the chrome injected date and time placeholder text */
      &::-webkit-datetime-edit {
        text-align: end;
      }
    `);
};
export const sharedCheckCss = css(["line-height:", ";color:inherit;margin:0;padding:0;appearance:none;border:1px solid ", ";min-height:1.125rem;min-width:1.125rem;max-width:1.125rem;max-height:1.125rem;background-color:", ";background-repeat:no-repeat,no-repeat;background-position:50% 50%,top left;", " ", " ", " &:disabled{border-color:", ";background-color:", ";cursor:not-allowed;}&:checked,&[type=\"checkbox\"]:indeterminate{background-color:", ";border-color:", ";&:disabled{border-color:", ";background-color:", ";}}"], lineHeight, borderColorPrimary, backgroundColorLevelCurrent, formElementDisabledBackgroundDef, formElementDisabledBorderDef, formElementDisabledSelectedBackgroundDef, formElementDisabledBorder, formElementDisabledBackground, accentColorPrimary, accentColorPrimary, formElementDisabledBorder, formElementDisabledSelectedBackground);
export const sharedInputCss = css(["color:", ";background-color:", ";color-scheme:", ";border-radius:", ";font-size:", ";font-family:", ";line-height:", ";padding:", ";width:100%;min-width:0;background-repeat:no-repeat;background-size:1.5rem 1.5rem;", ";&::placeholder{color:", ";}", " ", " &:disabled{border-color:", ";background-color:", ";color:", ";resize:none;cursor:not-allowed;}&:not(:where(select,:disabled)):read-only{border:none;resize:none;}"], textColorPrimary, backgroundColorLevelCurrent, colorScheme, borderRadius, fontSize16, sans, lineHeight, spacing8, ({
  $validityState,
  $small,
  $curAppearanceContext
}) => {
  const {
    borderColor,
    background,
    modeStyle
  } = valuesByValidityState[$validityState] || {};
  return css(["background-image:", ";padding-inline-end:", ";border:1px solid ", ";background-position:right ", " top ", ";", " height:", ";"], background ? darkModeURLIcon(background, modeStyle, $curAppearanceContext) : "", background ? "2.5rem" : spacing8, borderColor || borderColorPrimary, $small ? spacing4 : spacing8, $small ? spacing4 : spacing8, dir("rtl")`
        background-position: left ${$small ? spacing4 : spacing8} top
          ${$small ? spacing4 : spacing8}
      `, $small ? spacing32 : "2.5rem");
}, textColorTertiary, formElementDisabledBackgroundDef, formElementDisabledBorderDef, formElementDisabledBorder, formElementDisabledBackground, formElementDisabledColor);